import { useApprovingInvoiceReportBusinessDocumentService } from '@/context/services/approving_invoice_reports/ApprovingInvoiceReportBusinessDocument.service';
import { useApprovingInvoiceReportsSearchRefetch } from '@/context/services/approving_invoice_reports/refetchContext';
import { useGetEDocPlan } from '@/context/services/e_doc_plan/EDocPlan.service';
import { useCheckApprovingReportsProtectedResources } from '@/features/approving_invoice_reports/components/hooks/useCheckApprovingReportsProtectedResources';
import { useGetScanDecision } from '@/features/invoices/components/Details/Components/hooks/useScanDecision';
import { useTranslation } from '@/i18n';
import { useToastAPI } from '@moneyforward/ap-frontend-components';
import { useCallback, useEffect, useMemo } from 'react';
import { useBusinessDocumentInfo } from '../../hooks/useBusinessDocumentInfo';

const statusCodesCheckAccessPermission = ['401', '403'];

export const useFileInfoDrawerData = (
  businessDocumentId?: string,
  onCloseDetailDrawer?: () => void
) => {
  const { t } = useTranslation();
  const notify = useToastAPI();
  const refetchApprovingInvoiceReportsSearch =
    useApprovingInvoiceReportsSearchRefetch();
  const { handleCheckPermission } =
    useCheckApprovingReportsProtectedResources();

  const { data: businessDoc, error } =
    useApprovingInvoiceReportBusinessDocumentService(businessDocumentId ?? '');
  const { apReceiptTypeAlertType } = useGetEDocPlan();

  const eDocMetaDatum = businessDoc?.e_doc_meta_datum;
  const receiptType = businessDoc?.receipt_type;

  const scanDecision = useGetScanDecision(
    eDocMetaDatum,
    apReceiptTypeAlertType,
    true
  );

  const labels = useBusinessDocumentInfo(businessDoc);

  const eDocMetaProps = useMemo(() => {
    if (!eDocMetaDatum) return null;

    return {
      dpi: eDocMetaDatum.dpi_text ?? '',
      pixelNumber: eDocMetaDatum.pixel_number_text ?? '',
      imageDepth: eDocMetaDatum.rgb_depth_text ?? '',
      type:
        scanDecision === 'warning' || scanDecision === 'error'
          ? scanDecision
          : undefined,
      isScan: eDocMetaDatum.is_scan ?? false,
      isSatisfyResolutionRequirement:
        eDocMetaDatum.is_satisfy_resolution_requirement ?? false,
      isSatisfyDepthRequirements:
        eDocMetaDatum.is_satisfy_depth_requirements ?? false,
      receiptType: receiptType ?? undefined,
    };
  }, [eDocMetaDatum, scanDecision, receiptType]);

  const leftRightRatio = useMemo(
    () => ({
      left: { value: '35%' },
      right: { value: '65%' },
    }),
    []
  );

  const navigate = useMemo(() => {
    if (!businessDoc?.id) return '';
    const url = new URL('/business_documents', window.location.href);
    url.searchParams.append('id', businessDoc.id);
    return url.toString();
  }, [businessDoc?.id]);

  const showErrorMessages = useCallback(
    (message?: string) => {
      notify.error({
        title: message || t('system_error'),
        duration: 3,
      });
    },
    [notify, t]
  );

  const onCheckAccessApprovingReportSuccess = useCallback(
    () => () => {
      showErrorMessages(error?.messages?.join(','));
      if (onCloseDetailDrawer) {
        onCloseDetailDrawer();
      }
    },
    [showErrorMessages, onCloseDetailDrawer, error?.messages]
  );

  const onCheckAccessApprovingReportFailure = useCallback(() => {
    if (onCloseDetailDrawer) {
      onCloseDetailDrawer();
    }
  }, [onCloseDetailDrawer]);

  useEffect(() => {
    if (!error) return;
    if (statusCodesCheckAccessPermission.includes(error?.code || '')) {
      handleCheckPermission({
        fnCheckPermission: refetchApprovingInvoiceReportsSearch,
        onCheckSuccess: onCheckAccessApprovingReportSuccess(),
        onCheckFailure: onCheckAccessApprovingReportFailure,
      });
    } else {
      showErrorMessages(error?.messages?.join?.('\n'));
    }
  }, [
    error,
    handleCheckPermission,
    refetchApprovingInvoiceReportsSearch,
    onCheckAccessApprovingReportSuccess,
    onCheckAccessApprovingReportFailure,
    showErrorMessages,
  ]);

  return {
    businessDoc,
    error,
    labels,
    eDocMetaProps,
    leftRightRatio,
    navigate,
    fileName: labels.fileName,
  };
};
